import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { LOCAL_TZ_VALUE } from '../utils/timezone';

const STORAGE_KEY = 'admin-fe.displayTimezone';

interface TimezoneContextValue {
  timeZone: string;
  setTimeZone: (tz: string) => void;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

const readStored = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? LOCAL_TZ_VALUE;
  } catch {
    return LOCAL_TZ_VALUE;
  }
};

/**
 * App-wide display-timezone preference, persisted per browser via localStorage.
 * A single provider means changing the zone on any page updates every time/date
 * rendered through `useTimezone` live.
 */
export const TimezoneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [timeZone, setTimeZoneState] = useState<string>(readStored);

  const setTimeZone = useCallback((tz: string) => {
    setTimeZoneState(tz);
    try {
      localStorage.setItem(STORAGE_KEY, tz);
    } catch {
      /* storage unavailable (private mode) — keep in-memory value only */
    }
  }, []);

  const value = useMemo(() => ({ timeZone, setTimeZone }), [timeZone, setTimeZone]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

/** Returns the current display zone ('' = local) and a setter that persists it. */
export const useTimezone = (): [string, (tz: string) => void] => {
  const ctx = useContext(TimezoneContext);
  if (!ctx) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return [ctx.timeZone, ctx.setTimeZone];
};
